import autobind from 'autobind-decorator';
import { inject, injectable } from 'inversify';
import { observable, computed, isArrayLike, isObservable } from 'mobx';
import * as uuid from 'uuid';

import { container } from 'src/inversify.config';
import { ContextService } from './context.service';
import { factory, OData, $data } from './data/JayDataContext';
import { ObjectUtils } from '../utils/object';
import { IdentityService } from './identity.service';

@injectable()
export abstract class DataService<TEntityType extends typeof $data.Entity, TEntity extends $data.Entity, TModel extends Model> implements DataSet<TModel> {
	@observable
	protected contextService: ContextService;
	private query: $data.Queryable<TEntity>;
	private predicate: (entity: TEntity) => boolean;
	private predicateParams: any;
	private entitySet: $data.EntitySet<TEntityType, TEntity>;
	private sorts: SortingModel = {};
	private filters: FiltersModel = {};
	private isArchived: boolean = false;

	@observable
	public searchKeywords: string;

	public params: { [key: string]: any } = {};

	@observable
	public items: TModel[] = [];

	@observable
	private deleted: TModel[];

	@computed
	public get item() {
		if (this.items.length == 0)
			return null;
		return this.items[0];
	}

	@computed
	public get isDirty(): boolean {
		return this.contextService.isDirty;
	}

	@computed
	public get isModelDirty(): boolean {
		return this.contextService.isDirty
			? this.items.some(e => e.entity.entityState != $data.EntityState.Unchanged) || (this.deleted ? this.deleted.length > 0 : false)
			: false;
	}

	@observable
	private _pageSize: number = 10;

	@computed
	public get pageSize() {
		return this._pageSize;
	};

	public set pageSize(value: number) {
		this._pageSize = value;
		this._currentPage = 1;
	}

	@observable
	private _currentPage: number = 1;

	@computed
	public get currentPage() {
		return this.count == 0 ? 0 : this._currentPage;
	};

	public set currentPage(value: number) {
		if (value < 1)
			value = this.count > 0 ? 1 : 0;
		if (value > this.totalPages)
			value = this.totalPages;
		this._currentPage = value;
	}

	@computed
	public get totalPages() {
		return Math.ceil(this.count / this.pageSize);
	};

	@observable
	private _count: number = 0;

	@computed
	public get count() {
		return this._count;
	}

	constructor(
		contextService: ContextService,
		private getEntitySet: (context: OData.Container) => $data.EntitySet<TEntityType, TEntity>,
		private saveAsDelete: boolean = true,
	) {
		this.contextService = contextService || container.resolve(ContextService);
		this.configure(getEntitySet);
	}

	@autobind
	public nextPage() {
		this.currentPage++;
		try {
			return this.reload();
		} catch (error) {
			this.handleError(error);
		}
	}

	@autobind
	public previousPage() {
		this.currentPage--;
		try {
			return this.reload();
		} catch (error) {
			this.handleError(error);
		}
	}

	@autobind
	public firstPage() {
		this.currentPage = 1;
		try {
			return this.reload();
		} catch (error) {
			this.handleError(error);
		}
	}

	@autobind
	public lastPage() {
		this.currentPage = Math.ceil(this.count / this.pageSize);
		try {
			return this.reload();
		} catch (error) {
			this.handleError(error);
		}
	}

	@autobind
	public createSync(entityConstructor: () => TEntity): TModel {
		let entity = entityConstructor();
		entity = this.entitySet.add(entity);
		const item = this.map(entity);
		this.items.push(item);
		this._count++;
		item.entity = this.entitySet.attachOrGet(entity);
		this.contextService.registerEntityChanges(entity, true);
		return item;
	}

	@autobind
	public async create(entityConstructor: () => TEntity): Promise<TModel> {
		try {
			await this.contextService.current();
			return this.createSync(entityConstructor);
		} catch (error) {
			this.handleError(error);
		}
	}

	@autobind
	public async find(predicate: (entity: TEntity) => boolean, params?: {}): Promise<TModel> {
		this.predicate = predicate;
		this.predicateParams = params;
		try {
			await this.contextService.current();
			const entity = await this.entitySet.first(predicate, params);
			this.items = [];
			this._count = 1;
			const item = this.map(entity);
			this.items.push(item);
			this.entitySet.detach(item.entity);
			item.entity = this.entitySet.attachOrGet(entity);
			this.contextService.registerEntityChanges(entity);
			return item;
		} catch (error) {
			this.handleError(error);
		}
	}

	public async delete(items: TModel[]): Promise<number> {
		try {
			const context = await (this.saveAsDelete
				? container.resolve(ContextService).current()
				: this.contextService.current());

			const currentSet = this.getEntitySet(context);
			items.forEach((item) => {
				if (item.entity.entityState == 20) {
					this.entitySet.detach(item.entity);
					this.items.splice(this.items.indexOf(item), 1);
				} else {
					currentSet.remove(item.entity);
				}
			});
			this.contextService.registerEntityChanges(null, true);
			this._count -= items.length;

			if ((context.stateManager as any).trackedEntities.length > 0 && this.saveAsDelete) {
				const n = await context.saveChanges();
				items.forEach(item => this.entitySet.detach(item.entity));
				await this.reload();
				return n;
			} else {
				this.deleted = items;
				this.items = this.items.filter(e => items.every(i => i != e));
				this._count = this.items.length;
				return 0;
			}
		} catch (error) {
			this.handleError(error);
		}
	}

	@autobind
	public async restore(items: TModel[]): Promise<number> {
		try {
			const context = await (this.saveAsDelete
				? container.resolve(ContextService).current()
				: this.contextService.current());

			const currentSet = this.getEntitySet(context);
			items.forEach((item) => {
				currentSet.attach(item.entity);
				item.entity.isDeleted = false;
			});
			this.contextService.registerEntityChanges(null, true);
			this._count -= items.length;
			this.contextService.registerEntityChanges(null, true);
			this._count -= items.length;

			if ((context.stateManager as any).trackedEntities.length > 0 && this.saveAsDelete) {
				const n = await context.saveChanges();
				items.forEach(item => this.entitySet.detach(item.entity));
				await this.reload();
				return n;
			} else {
				this.deleted = items;
				this.items = this.items.filter(e => items.every(i => i != e));
				this._count = this.items.length;
				return 0;
			}
		} catch (error) {
			this.handleError(error);
		}
	}

	@autobind
	public async save(): Promise<number> {
		try {
			const context = await this.contextService.current();
			const tracked: { data: $data.Entity; entitySet: $data.EntitySet<any, any>; }[] = [];
			if ((context.stateManager as any).trackedEntities) {
				(context.stateManager as any).trackedEntities.forEach((change: { data: $data.Entity; entitySet: $data.EntitySet<any, any>; }) => {
					const entity = (change.data as any);
					tracked.push(change);
					if (
						entity.dynamicProperties != null &&
						change.data.entityState > 10 &&
						change.data.entityState < 40 &&
						change.data.changedProperties &&
						change.data.changedProperties.some(m => m.name === "id")
					) {
						//if ((change.data.entityState == $data.EntityState.Added || change.data.entityState == $data.EntityState.Modified)
						//	|| change.data.changedProperties.some(p => p.enumerable)) {
						entity.dynamicProperties = ObjectUtils.deepCopy(entity.dynamicProperties, attr => attr == '__isProxy' || attr == '__edit');
						//}
					}
				})
			}
			const n = await context.saveChanges();
			for (const entity of tracked) {
				entity.entitySet.attach(entity.data);
			}
			this.deleted = [];
			return n;
		} catch (error) {
			if (Array.isArray(error)) {
				let message = error.map(e => typeof e === "string" ? e : e.errors ? `${e.item.constructor.name} ${e.errors.map(v => v.PropertyDefinition.name + "(" + v.Type + ")").join("<br/>")}` : e.toString()).join("<br />");
				throw new Error(message);
			} else {
				this.handleError(error);
			}
		}
	}

	@autobind
	public async configure(entitySet: (context: OData.Container) => $data.EntitySet<TEntityType, TEntity>) {
		try {
			if (!this.contextService) {
				throw 'DataService requires a valid contextService';
			}
			const context = await this.contextService.current()
			this.entitySet = entitySet(context);
			if (!this.entitySet) {
				throw 'DataService requires a valid entitySet';
			}
		} catch (error) {
			this.handleError(error);
		}
	}

	@autobind
	public async load(query?: (entitySet: $data.EntitySet<TEntityType, TEntity>) => $data.Queryable<TEntity>): Promise<TModel[]> {
		try {
			await this.contextService.current()
			if (query) {
				this.query = query(this.entitySet);
			}
			if (!this.query) {
				throw 'A Query is required';
			}
			return this.applyQuery(this.query);
		} catch (error) {
			this.handleError(error);
		}
	}

	@autobind
	public toggleArchive() {
		this.isArchived = !this.isArchived;
		return this.reload();
	}

	@autobind
	public async reload(): Promise<TModel[]> {
		if (this.query) {
			try {
				return this.load();
			} catch (error) {
				this.handleError(error);
			}
		}
		else {
			try {
				const model = await this.find(this.predicate, this.predicateParams);
				return [model];
			} catch (error) {
				this.handleError(error);
			}
		}
	}

	@autobind
	public applySorting<TEntity>(query: $data.Queryable<TEntity>): $data.Queryable<TEntity> {
		for (let field in this.sorts) {
			const def = this.sorts[field].default;
			if (def && Object.getOwnPropertyNames(this.sorts).length > 1) {
				continue;
			}
			const dir = this.sorts[field].dir;
			const ev = eval(`e => e.${field}`);
			query = dir == 'asc' ? query.orderBy(ev) : query.orderByDescending(ev);
		}
		return query;
	}

	public addSorting<K extends keyof TModel>(key: K, dir: 'asc' | 'desc', isDefault?: boolean): void {
		this.sorts[key.toString()] = {
			dir: dir,
			default: isDefault
		};
	}

	public removeSorting<K extends keyof TModel>(key: K): void {
		delete this.sorts[key.toString()];
	}

	public hasSorting<K extends keyof TModel>(key: K): boolean {
		return this.sorts[key.toString()] != null;
	}

	@autobind
	public applyFilter<TEntity>(query: $data.Queryable<TEntity>): $data.Queryable<TEntity> {
		for (const field in this.filters) {
			const filter = this.filters[field];
			if (filter.value === undefined) {
				continue;
			}
			const params = {};
			params[filter.fieldName] = filter.value;
			switch (filter.operator) {
				case undefined:
				case null:
				case 'eq':
					query = query.filter(eval(`e => e.${filter.fieldName} == ${filter.fieldName}`), params);
					break;
				case 'ne':
					query = query.filter(eval(`e => e.${filter.fieldName} != ${filter.fieldName}`), params);
					break;
				case 'gt':
					query = query.filter(eval(`e => e.${filter.fieldName} > ${filter.fieldName}`), params);
					break;
				case 'ge':
					query = query.filter(eval(`e => e.${filter.fieldName} >= ${filter.fieldName}`), params);
					break;
				case 'lt':
					query = query.filter(eval(`e => e.${filter.fieldName} < ${filter.fieldName}`), params);
					break;
				case 'le':
					query = query.filter(eval(`e => e.${filter.fieldName} <= ${filter.fieldName}`), params);
					break;
				case 'endsWith':
					query = query.filter(`e => e.${filter.fieldName}.endsWith(${filter.fieldName})`, params);
					break;
				case 'startsWith':
					query = query.filter(`e => e.${filter.fieldName}.startsWith(${filter.fieldName})`, params);
					break;
				case 'contains':
					query = query.filter(`e => e.${filter.fieldName}.contains(${filter.fieldName})`, params);
					break;
				case 'in':
					query = query.filter(`e => e.${filter.fieldName} in ${filter.fieldName}`, params);
					break;
				case 'not-in':
					query = query.filter(`e => !(e.${filter.fieldName} in ${filter.fieldName})`, params);
					break;
				default:
					throw new Error('Unsuported operation!');
			}
		}
		return query;
	}

	public addFilter<K extends keyof TModel>(key: K, operator: FilterOperator, value: any, filtertype?: string): void {
		if (filtertype == null) {
			const keyString = key.toString();
			this.filters[keyString] = {
				fieldName: keyString,
				operator: operator === undefined && this.filters[keyString] ? this.filters[keyString].operator : operator,
				value: value === undefined && this.filters[keyString] ? this.filters[keyString].value : value
			};

		}
		else if (filtertype == "params") {
			this.params[key.toString()] = value;
		}
	}

	public removeAllFilter() {
		this.filters = {};
	}

	public removeFilter<K extends keyof TModel>(key: K, filtertype?: string): void {
		if (filtertype == null)
			delete this.filters[key.toString()];
		else if (filtertype == "params") {
			delete this.params[key.toString()];
		}
	}

	public hasFilter<K extends keyof TModel>(key: K, hasValue = false, filtertype?: string): boolean {
		if (filtertype == null) {
			const filter = this.filters[key.toString()];
			return filter != null && hasValue ? filter.value !== undefined : filter != null;
		}
		else if (filtertype == "params") {
			const param = this.params[key.toString()];
			return param != null;
		}
	}

	protected abstract map(entity: TEntity): TModel;

	@autobind
	private async applyQuery(query: $data.Queryable<TEntity>): Promise<TModel[]> {
		var query = this.query;
		//https://github.com/jaystack/jaydata/issues/289
		(<any>$data.EntityContext.prototype).searchKeywords = this.searchKeywords;
		(<any>$data.EntityContext.prototype).params = this.params;
		query = this.applyFilter(query);
		query = this.applySorting(query);
		query = query.withCount();
		query = query.take(this.pageSize);
		query = query.skip((this.currentPage == 0 ? 0 : this.currentPage - 1) * this.pageSize);
		const result = await query.toArray();
		this._count = (<any>result).totalCount;
		this.items = result.map((item) => {
			const model = this.map(item);
			this.entitySet.detach(model.entity);
			model.entity = this.entitySet.attachOrGet(item);
			return model;
		});
		this.contextService.registerEntityChanges(result);
		return this.items;
	}

	@autobind
	private handleError(reject: { data: { message: string; response: { statusCode: number; } }[] }) {
		if (reject.data[0] && reject.data[0].response) {
			switch (reject.data[0].response.statusCode) {
				case 401:
					const identity = container.resolve(IdentityService);
					setTimeout(identity.logOff, 3000);
					throw new Error("Access is denied, please enter your credentials and try again. If it fails, contact your administrator");
				default:
					throw new Error(`Unknown error: ${reject.data[0].response.statusCode}, please contact your administrator`);
			}
		}
		throw new Error(`Unknown error, please contact your administrator`);
	}
}

export function property<Entity>(getter: (o: Entity) => any, setter: (o: Entity, value: any) => any) {
	return function (target: Object, key: string): void {
		// property getter
		var getterWrapper = function () {
			try {
				var val = this[`_${key}`] || getter(this);
				if (val != null && val instanceof Object && !Array.isArray(val) && !val.__isProxy && !isObservable(val) && !(val instanceof $data.Entity) && !(val instanceof Date) && !(val instanceof Uint8Array)) {
					val = proxify(this[`_${key}`] || getter(this), this);
					this[`_${key}`] = val;
				}
			} catch (err) {
				console.log(`BINDING ERROR: ${target.constructor.name}.${key} => ${err}`);
				return undefined;
			}
			return val;
		};

		// property setter
		var setterWrapper = function (newVal) {
			try {
				this[`_${key}`] = newVal;
				setter(this, newVal);
			} catch (err) {
				console.log(`BINDING ERROR: ${target.constructor.name}.${key} => ${err}`);
			}
			console.log(`SET: ${target.constructor.name}.${key} => ${newVal}`);
		};

		// Delete property.
		if (delete target[key]) {
			observable(target, `_${key}`);
			// Create new property with getter and setter
			var newSetter = setterWrapper;
			var newGetter = getterWrapper;
			Object.defineProperty(target, key, {
				get: newGetter,
				set: newSetter,
				enumerable: true,
				configurable: true
			});
			observable()
			computed(target, key, Object.getOwnPropertyDescriptor(target, key));
		}
	}
}

function tryProxify(target, property, entity = undefined) {
	let value = target[property];
	try {
		if (property[0] != '$' && property[0] != '_' && property[0] != '@' && property != 'constructor' && !isObservable(target, property)) {
			if (value != null && typeof value == 'object' && !(value instanceof Date)) {
				value = proxify(value, entity);
			}
			observable(target, property);
			if (Array.isArray(value) || (isArrayLike(target) && typeof value == 'object')) {
				value = observable(value);
			}
			target[property] = value;
		}
	} catch (err) {
		debugger;
	}
	return value;
}

function proxify(obj, entity = undefined) {
	const proxyHandler = {
		get: function (target, property) {
			if (isArrayLike(target)) {
				return target[property];
			}
			if (property in target) {
				return tryProxify(target, property, entity);
			}
			return undefined;
		},
		set: function (target, property, value, receiver) {
			if (Object.getOwnPropertyNames(target).every(name => name != property)) {
				observable(target, property);
				if (value != null && typeof value == 'object' && !(value instanceof Date)) {
					value = proxify(value, entity);
				}
			}
			target[property] = value;
			if (entity != null && entity.dynamicProperties != null && typeof value != "function") {
				//entity.dynamicProperties = ObjectUtils.deepCopy(entity.dynamicProperties, attr => attr == '__isProxy' || attr == '__edit');
				if (typeof entity.id === "string") {
					const guid = uuid.v4();
					const oldId = entity.id;
					entity.id = guid;
					entity.id = oldId;
				} else {
					const id = entity.id == null ? -1 : entity.id;
					entity.id = id + 1;
					entity.id--;
				}
				// HACK: Cannot change dynamicProperties, because in some components, like contract, the behaviour is broken
				// so changing PK instead
			}
			return true;
		}
	};

	try {
		obj.__isProxy = true;
	} catch (err) {
		debugger;
	}
	for (let property in obj) {
		tryProxify(obj, property, entity);
	}
	observable(obj);
	return new Proxy(obj, proxyHandler);
};



// WEBPACK FOOTER //
// ./src/core/services/data.service.ts